import React, { useState, useEffect } from 'react';
import { X, TrendingUp, DollarSign, Users, GraduationCap, FileText, ExternalLink, Loader2, BookOpen, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { api } from '../api';

const TABS = [
  { id: 'overview', label: 'Overview', icon: GraduationCap },
  { id: 'mobility', label: 'Mobility', icon: TrendingUp },
  { id: 'essays', label: 'Essays', icon: FileText },
];

function Stat({ label, value, hint }) {
  return (
    <div className="bg-[#F9F9F7] rounded-lg p-4">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
      <p className="text-xl font-semibold text-[#0F392B] mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

/** Horizontal bar showing student earnings vs parent income vs national median */
function EarningsBar({ studentEarnings, parentIncome }) {
  // Scale 0 → 200k
  const max = 200000;
  const pPct = Math.min(100, ((parentIncome || 0) / max) * 100);
  const sPct = Math.min(100, ((studentEarnings || 0) / max) * 100);
  return (
    <div className="space-y-3">
      <div>
        <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
          <span>Median parent income</span>
          <span className="font-mono text-[#0F392B] font-semibold">${(parentIncome || 0).toLocaleString()}</span>
        </div>
        <div className="h-2 bg-[#F2F2F0] rounded-full overflow-hidden">
          <div className="h-full bg-[#D4AF37] rounded-full" style={{ width: `${pPct}%` }} />
        </div>
      </div>
      <div>
        <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
          <span>Median student earnings (age ~34)</span>
          <span className="font-mono text-[#0F392B] font-semibold">${(studentEarnings || 0).toLocaleString()}</span>
        </div>
        <div className="h-2 bg-[#F2F2F0] rounded-full overflow-hidden">
          <div className="h-full bg-[#0F392B] rounded-full" style={{ width: `${sPct}%` }} />
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground pt-1 border-t border-[#E2E2E2]">
        Scale: $0 → $200,000 · National median household income ≈ $75,000
      </div>
    </div>
  );
}

/** Income-quintile breakdown — represents the famous NYT bar */
function IncomeQuintile({ bottomPct, topPct }) {
  const pct = Math.max(0, Math.min(100, bottomPct || 0));
  const topPctNum = Math.max(0, Math.min(100, topPct || 0));
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <p className="text-xs text-muted-foreground">Share of students from <span className="text-[#0F392B] font-medium">bottom 20%</span> of income</p>
        <p className="text-xl font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>{pct.toFixed(1)}%</p>
      </div>
      <div className="h-3 bg-[#F2F2F0] rounded-full overflow-hidden relative">
        <div className="h-full bg-gradient-to-r from-[#0F392B] to-[#1a5c44] rounded-full transition-all" style={{ width: `${Math.min(pct * 2, 100)}%` }} />
      </div>
      <div className="flex items-baseline justify-between pt-2">
        <p className="text-xs text-muted-foreground">Share from <span className="text-[#D4AF37] font-medium">top 1%</span></p>
        <p className="text-xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{topPctNum.toFixed(1)}%</p>
      </div>
      <div className="h-3 bg-[#F2F2F0] rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#c49b2f] rounded-full transition-all" style={{ width: `${Math.min(topPctNum * 4, 100)}%` }} />
      </div>
      <p className="text-[10px] text-muted-foreground italic">
        For context: if access were perfectly equal, every quintile would represent 20% of students.
      </p>
    </div>
  );
}

export default function SchoolDetailModal({ school, onClose }) {
  const [tab, setTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!school?.name) return;
    setLoading(true);
    api.getSchoolDetail(school.name)
      .then(setDetail)
      .catch(() => setDetail(school))
      .finally(() => setLoading(false));
  }, [school?.name]);

  if (!school) return null;
  const d = detail || school;
  const m = d.mobility;

  return (
    <div className="fixed inset-0 z-50 bg-[#0F392B]/50 backdrop-blur-sm flex items-center justify-center px-4 py-8" onClick={onClose} data-testid="school-detail-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#E2E2E2] flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-[#0F392B] truncate" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="modal-school-name">{d.name}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{d.state} · {d.region}{d.acceptance_rate ? ` · ${d.acceptance_rate}% acceptance` : ''}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F2F2F0] rounded-lg transition-colors" data-testid="modal-close-btn">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 border-b border-[#E2E2E2] flex gap-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium rounded-t-md transition-all border-b-2 ${tab === t.id ? 'text-[#0F392B] border-[#0F392B]' : 'text-muted-foreground border-transparent hover:text-foreground'}`}
              data-testid={`modal-tab-${t.id}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="text-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#0F392B] mx-auto" />
            </div>
          )}

          {!loading && tab === 'overview' && (
            <div className="space-y-6" data-testid="modal-overview">
              {/* Quick stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="SAT Mid-50%" value={d.sat_25 && d.sat_75 ? `${d.sat_25}–${d.sat_75}` : '—'} />
                <Stat label="ACT Mid-50%" value={d.act_25 && d.act_75 ? `${d.act_25}–${d.act_75}` : '—'} />
                <Stat label="Acceptance" value={d.acceptance_rate ? `${d.acceptance_rate}%` : '—'} />
                <Stat label="Net Price" value={d.net_price ? `$${(d.net_price / 1000).toFixed(0)}k` : '—'} hint="After typical aid" />
              </div>

              {/* Deadlines */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Deadlines · {d.deadline_cycle || ''}</p>
                <div className="space-y-1.5">
                  {d.ed && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Early Decision</span><span className="font-mono font-medium">{d.ed}</span></div>}
                  {d.ea && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Early Action</span><span className="font-mono font-medium">{d.ea}</span></div>}
                  {d.rd && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Regular Decision</span><span className="font-mono font-medium">{d.rd}</span></div>}
                </div>
              </div>

              {/* Platform */}
              {d.platform && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Application Platforms</p>
                  <div className="flex flex-wrap gap-1.5">
                    {d.platform.split(', ').map(p => (
                      <Badge key={p} variant="outline" className="text-[10px] bg-[#F9F9F7]">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {d.url && (
                <a href={d.url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full text-xs" data-testid="modal-school-website">
                    <ExternalLink className="h-3 w-3 mr-1.5" /> Visit Admissions Website
                  </Button>
                </a>
              )}
            </div>
          )}

          {!loading && tab === 'mobility' && (
            <div className="space-y-6" data-testid="modal-mobility">
              {m ? (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-[#0F392B] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Who attends?</h3>
                    <IncomeQuintile bottomPct={m.share_from_bottom_20_pct} topPct={m.share_from_top_1_pct} />
                  </div>

                  <div className="border-t border-[#E2E2E2] pt-6">
                    <h3 className="text-sm font-semibold text-[#0F392B] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Income outcomes</h3>
                    <EarningsBar studentEarnings={m.median_student_earnings} parentIncome={m.median_parent_income} />
                  </div>

                  <div className="border-t border-[#E2E2E2] pt-6 grid grid-cols-2 gap-3">
                    <Stat
                      label="Mobility Success Rate"
                      value={m.success_rate_bottom_to_top_quintile ? `${m.success_rate_bottom_to_top_quintile}%` : '—'}
                      hint="of low-income students reach top 20% by mid-30s"
                    />
                    <Stat
                      label="Overall Mobility Rate"
                      value={m.mobility_rate ? `${m.mobility_rate}%` : '—'}
                      hint="access × success"
                    />
                  </div>

                  <p className="text-[10px] text-muted-foreground italic pt-2 border-t border-[#E2E2E2]">
                    {m.source}. The same dataset behind the NYT College Mobility tool.
                  </p>
                </>
              ) : (
                <div className="text-center py-10">
                  <BarChart3 className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Mobility data not available for this institution.</p>
                  <p className="text-[11px] text-muted-foreground mt-1">Some schools were grouped or redacted in the original dataset.</p>
                </div>
              )}
            </div>
          )}

          {!loading && tab === 'essays' && (
            <div className="space-y-6" data-testid="modal-essays">
              {/* Supplements */}
              <div>
                <h3 className="text-sm font-semibold text-[#0F392B] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{d.name} Supplements <span className="text-muted-foreground font-normal">· 2025–2026</span></h3>
                {d.supplemental_essays && d.supplemental_essays.length > 0 ? (
                  <ul className="space-y-2">
                    {d.supplemental_essays.map((p, i) => (
                      <li key={`supp-${i}`} className="flex gap-3 text-xs">
                        <span className="text-[#D4AF37] font-mono mt-0.5">{i + 1}.</span>
                        <span className="text-foreground leading-relaxed">{p}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No additional supplemental essays — Common App essay only.</p>
                )}
              </div>

              {/* Common App */}
              <div className="border-t border-[#E2E2E2] pt-5">
                <h3 className="text-sm font-semibold text-[#0F392B] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Common App Prompts <span className="text-muted-foreground font-normal">· choose one · 250–650 words</span></h3>
                {d.common_app_prompts && d.common_app_prompts.length > 0 ? (
                  <ol className="space-y-3">
                    {d.common_app_prompts.map((p, i) => (
                      <li key={`ca-${i}`} className="flex gap-3 text-xs">
                        <span className="text-[#D4AF37] font-mono mt-0.5 flex-shrink-0">{i + 1}.</span>
                        <span className="text-foreground/80 leading-relaxed">{p}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Prompts not loaded.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
